import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { i18n } from "i18next";
import { Alert, Button, DialogActions, DialogContent, DialogTitle, Divider, Stack } from "@mui/material";

import TabbedDialog from "components/dialogs/TabbedDialog";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import { AutoWidgetQueryBehavior, getWidgetResponsesByName, WidgetRequest } from "system/widget/Widget";
import { filterQueryFields, QueryField, QueryFieldOption, QueryInfo } from "system/query/QueryInfo";
import { Agg, OpOption } from "system/query/Aggs";
import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import { toTitleCase } from "components/formatting/TitleCase";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsCheckbox from "components/settingsConfig/uiComponents/SettingsCheckbox";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import ColumnManager, { densityOptions } from "components/tables/columns/ColumnManager";
import { initialColumnModel } from "./QueryComparisonTable";
import { ColumnDialogMode, ColumnDialogParams, ColumnModel, ColumnModelItem, ColumnRenderType, ColumnToolbarType } from "components/tables/columns/ColumnModel";
import ComparisonTableDialogContent from "./ComparisonTableDialogContent";
import { queryComparisonTableDefaultSettings, QueryComparisonTableSettingsType } from "./comparisonTableUtils";
import QueryComparisonTableToolbar from "./QueryComparisonTableToolbar";
import APIRequest from "system/APIRequest";
import { socialDatasetId } from "system/query/Query";

export type ComparisonDraftColumn = {
  field: QueryField,
  operator?: OpOption,
  option?: QueryFieldOption,
  activeColumnIndex?: number,
  renderType?: ColumnRenderType,
}

const defaultWidgetRequest = {
  name: 'query-comparison-table',
  endpoint: 'query/agg',
  queryParams: { include_total: true },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_MULTI_ALWAYS
} as WidgetRequest;

export function buildDefaultQueriesRequest(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  const foundKeywordfields = queryInfo?.fields?.filter((field: QueryField) => {
    return ["keyword", "keywords"].includes(field.type) && field.options?.length;
  }) || false;

  const widgetRequest: WidgetRequest = { ...defaultWidgetRequest };

  if (foundKeywordfields?.length) {
    const foundKeywordField = foundKeywordfields.find((field: QueryField) => {
      return field.id === "sentiment";
    }) || foundKeywordfields[0];

    if (foundKeywordField && foundKeywordField.id && foundKeywordField.label) {
      const requestInitBody = {
        aggs: {
          [foundKeywordField.label]: {
            "op":"keyword",
            "field": foundKeywordField.id,
            "aggs": {
              "percent": {
                "op": "percent",
                "of_total": true
              }
            }
          }
        }
      };

      widgetRequest.requestInit = { body: requestInitBody };
      return [widgetRequest];
    }
  }
  return undefined;
}

const getAggsFromColumns = (columns: ColumnModelItem[]) => {
  const groupedAggs = columns.reduce((acc: Record<string, Agg>, curr: ColumnModelItem) => {
    if (curr.sourceField === "_count") {
       return acc;
    }

    if (curr.sourceField === "social_universe") {
      acc[curr.sourceField] = {
        op: "social_universe"
      };

      return acc;
    }

    // If field is already added, add filter option to filter list
    // If not, add agg like normal
    if (acc[curr.sourceField]) {
      if (curr.filter) {
        acc[curr.sourceField].filter = [
          ...(acc[curr.sourceField].filter || []),
          ...(curr.filter || [])
        ];
      }

      if (curr.operator) {
        // If aggs is integer type the "op" value will be of min, max, sum, avg, etc
        acc[curr.sourceField].op = curr.operator;
      }
    } else {
      acc[curr.sourceField] = {
        field: curr.sourceField,
        ...(curr.operator && { op: curr.operator }),
        ...(curr.filter &&
          {
            filter: curr.filter,
            // Some aggs that have a "filter" prop will have "keyword" for the "op" value
            op: curr.fieldType
          }
        )
      };
    }

    return acc;
  }, {});

  return Object.values(groupedAggs);
};

// Returns a unique field id for each column. DataGrid component requires unique ids.
export const getUniqueFieldId = (columns: ColumnModelItem[], stagedColumn: ComparisonDraftColumn) => {
  let maxSuffix = 0;
  let baseIdName = "";

  if (stagedColumn.operator) {
    baseIdName = `${stagedColumn.field.id} (${stagedColumn.operator.id})`;
  } else if (stagedColumn.option) {
    baseIdName = `${stagedColumn.field.id} (${stagedColumn.option.id})`;
  } else {
    baseIdName = stagedColumn.field.id;
  }

  for (const { id } of columns) {
    if (id === baseIdName) {
      // If column item id & baseIdName match, get the max suffix between 1 and the current maxSuffix
      maxSuffix = Math.max(maxSuffix, 1);
    } else if (id.startsWith(`${baseIdName}-`)) {
      // If column with "-number" in it already exists, assign extracted number value to suffix variable
      // Converts string number -> ("1") to an actual number -> (1)
      const suffix = parseInt(id.split(`${baseIdName}-`)[1], 10);
      if (!isNaN(suffix)) {
        // If suffix variable is number, assign maxSuffix to incremented suffix variable
        maxSuffix = Math.max(maxSuffix, suffix + 1);
      }
    }
  }

  return maxSuffix === 0 ? baseIdName : `${baseIdName}-${maxSuffix}`;
};

function handleDialogSubmit({
  columnDialogMode,
  columnModelState,
  draftColumn: stagedColumn,
  updateColumnModelState,
  i18n
}: {
  columnDialogMode?: ColumnDialogMode,
  columnModelState: ColumnModel,
  draftColumn: ComparisonDraftColumn,
  updateColumnModelState: (newColumnModel: ColumnModel) => void,
  i18n: i18n
}) {
  const uniqueId = getUniqueFieldId(columnModelState.columns, stagedColumn);
  let newColumnModelState: { columns: ColumnModelItem[] } = { columns: [] };
  let headerName = "";

  if (stagedColumn.operator) {
    headerName = `${stagedColumn.field.label} (${i18n.t(`${stagedColumn.operator.id}`)})`;
  } else if (stagedColumn.option) {
    headerName = toTitleCase(stagedColumn.option.label);
  } else {
    headerName = stagedColumn.field.label;
  }

  let newColumn = {
    id: uniqueId,
    headerName,
    field: uniqueId,
    sourceField: stagedColumn.field.id,
    fieldType: stagedColumn.field.type,
    renderType: stagedColumn.renderType ?? ColumnRenderType.Number,
    hasHeaderOptions: true,
    showRenderTypeInSettings: false,
    ...(stagedColumn.operator && {
      operator: stagedColumn.operator.id
    }),
    ...(stagedColumn.option && {
      ...(stagedColumn.option.id && {
        filter: [stagedColumn.option.id],
      }),
      ...(stagedColumn.option.label && {
        labels: [stagedColumn.option.label],
      })
    })
  };

  if (columnDialogMode === ColumnDialogMode.ADD_COLUMN) {
    newColumnModelState = {
      ...columnModelState,
      columns: [...columnModelState.columns, newColumn]
    };
  }

  if (columnDialogMode === ColumnDialogMode.UPDATE_COLUMN) {
    if (stagedColumn.activeColumnIndex !== null && stagedColumn.activeColumnIndex !== undefined) {
      const columnsCopy = [...columnModelState.columns];

      columnsCopy[stagedColumn.activeColumnIndex] = newColumn;
      newColumnModelState = { ...columnModelState, columns: columnsCopy };
    }
  }

  updateColumnModelState(newColumnModelState);
}

export default function QueryComparisonTableSettings(props: WidgetSettingsProps) {
  const { i18n } = useTranslation();

  const apiResponses = getWidgetResponsesByName(props.apiResponses, "query-comparison-table");

  // Using own query info =====
  const [queryInfo, setQueryInfo] = useState<QueryInfo>();

  const foundKeywordfields = queryInfo?.fields?.filter((field: QueryField) => {
    return ["keyword", "keywords"].includes(field.type) && field.options?.length;
  }) || [];

  let filteredQueryFields = filterQueryFields(
    queryInfo?.fields ?? [],
    { allowedFieldTypes: ["decimal", "integer", "keyword", "keywords"] }
  ).filter(f => !["source", "handle", "site", "links_to"].includes(f.id));

  const [draftColumn, setDraftColumn] = useState<ComparisonDraftColumn | undefined>(() => {
    const countField = queryInfo?.fields.find(f => f.id === "__count__");
    return countField ? { field: countField } : undefined;
  });

  const [tabValue, setTabValue] = useState<number>(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);
  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCloseDialog = () => {
    setDraftColumn(undefined);
    setIsDialogOpen(false);
  };

  const [settingsState, setSettingsState] = useState<QueryComparisonTableSettingsType>({...queryComparisonTableDefaultSettings, ...props?.widget?.data?.settings});
  const settingsOnChange = (updatedState: QueryComparisonTableSettingsType) => setSettingsState(updatedState);

  const [columnDialogMode, setColumnDialogMode] = useState<ColumnDialogMode>();
  const [activeColumnIndex, setActiveColumnIndex] = useState<number>();
  const handleSetDraftColumn = (col: ComparisonDraftColumn) => {
    setDraftColumn(col);
  };

  const [localAggs, setLocalAggs] = useState<Agg[]>(props.widget?.data?.columnModel?.columns
    ? getAggsFromColumns(props.widget?.data?.columnModel?.columns) : []);

  const _initialColumnModel = {
    columns: initialColumnModel.columns.map((colItem: ColumnModelItem) => {
      return {
        ...colItem,
        headerName: colItem.sourceField === "_count" ?
          i18n.t(colItem.headerName) :
          toTitleCase(colItem.headerName),
        hasHeaderOptions: true
      };
    }),
  };

  const [columnModelState, setColumnModelState] = useState<ColumnModel>({
    ..._initialColumnModel,
    ...props.widget?.data?.columnModel
  });

  function updateColumnModelState(newColumnModel: ColumnModel) {
    setLocalAggs(getAggsFromColumns(newColumnModel.columns));
    setColumnModelState(newColumnModel);
  }

  function handleColumnDialogMode(params: ColumnDialogParams) {
    if (params.mode === ColumnDialogMode.UPDATE_COLUMN) {
      const foundColumn = columnModelState.columns.find((col: ColumnModelItem) => col.id === params.activeField);
      const foundField = queryInfo?.fields.find(f => f.id.includes(params.activeField.split("(")[0].trim()));

      if (foundColumn && foundField) {
        const draftColumn = {
          field: foundField,
          ...(foundColumn?.operator && {
            operator: {
              id: foundColumn.operator,
              label: i18n.t(foundColumn.operator)
            }
          }),
          ...(foundColumn?.filter && {
            option: {
              id: foundColumn.filter[0],
              label: foundColumn.labels?.[0] ?? ""
            }
          }),
          activeColumnIndex: params.activeColumnIndex,
          ...(foundColumn.renderType && {
            renderType: foundColumn.renderType
          })
        };
        
        setDraftColumn(draftColumn);
        setActiveColumnIndex(params.activeColumnIndex);
      }
    }

    setColumnDialogMode(params.mode);
    handleOpenDialog();
  }

  function handleDeleteColumn(columnItem: ColumnModelItem) {
    const indexOfColumnToRemove = columnModelState.columns.findIndex((col: ColumnModelItem) => col.field === columnItem.field);
    const newModelColumnsList = columnModelState.columns.toSpliced(indexOfColumnToRemove, 1);
    const newColumnModelState = { ...columnModelState, columns: newModelColumnsList };
    setColumnModelState(newColumnModelState);
  }

  function deleteMultipleColumns(activeColumns: ColumnModelItem[]) {
    const updatedColumns = columnModelState.columns.filter((col: ColumnModelItem) => {
      return !activeColumns.includes(col);
    });

    const newColumnModelState = { ...columnModelState, columns: updatedColumns };
    setColumnModelState(newColumnModelState);
  }

  function updateWidget() {
    const requestInitBody = { aggs: {} };
    const queriesWidgetRequest: WidgetRequest = { ...defaultWidgetRequest };
    let requests: any[] = [];

    const uniqueAggs = localAggs.reduce((acc: any, agg: Agg) => {
      if (agg.op === "social_universe") {
        return {
          ...acc,
          // [`${socialUniverseField.label}`]: agg -- TODO: use (getSocialUniverseField()) once it's implemented
          ["social_universe"]: agg
        };
      }

      if (!agg.field || !agg.op) { return acc; }

      if (agg.filter) {
        if (agg.op === "keywords") {
          agg.op = "keyword";
        }

        agg["aggs"] = {
          "percent": {
            "op": "percent",
            "of_total": true
          }
        }

        return {
          ...acc,
          [agg.field]: agg
        };
      } else {
        return {
          ...acc,
          [`${agg.field} (${agg.op})`]: agg
        };
      }

    }, {});
    
    requestInitBody.aggs = uniqueAggs;

    if (Object.keys(requestInitBody.aggs).length) {
      queriesWidgetRequest.requestInit = { body: requestInitBody };
      
      // Save for when combined row request is functional
      // const combinedWidgetRequest = {
      //   ...queriesWidgetRequest,
      //   name: 'query-comparison-table-combined',
      //   autoWidgetQueryBehavior: "AUTO_SINGLE",
      // };
      
      requests = [queriesWidgetRequest];

      // Save for when combined row request is functional
      // if (settingsState.displayAllQueriesRow) {
      //   requests = [queriesWidgetRequest, combinedWidgetRequest];
      // }
    }

    props.saveWidgetDataAndRequests({
      columnModel: columnModelState,
      settings: settingsState
    }, requests).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    });
  }

  const aggFieldsInUse: string[] | [] = Array.from(new Set(columnModelState.columns.reduce((acc: any, curr: any) => {
    if (curr.sourceField === "_count" || curr.sourceField === "__count__") {
      return acc;
    }

    acc.push(curr.sourceField);
    return acc;
  }, [])));

  const usuableFields = filteredQueryFields.filter((item: QueryField, i, arr) => {
    if (aggFieldsInUse.length === arr.length) { return arr; }
      
    // if aggFieldsInUse is less than 3, any field can be added
    if (aggFieldsInUse.length < 3) { return arr };

    // if agg item in use has options use it
    if (aggFieldsInUse?.includes(item.id) && item.options) {
      return item;
    }
  });

  function handleBulkAddFieldOptions(fieldTitle: string) {

    const foundQueryField = queryInfo?.fields.find(f => f.id === fieldTitle);

    if (foundQueryField) {
      const optionsColumns = foundQueryField.options?.map((item) => {
        return {
          id: `${foundQueryField.id} (${item.id})`,
          headerName: toTitleCase(i18n.t(item.label)),
          field: `${foundQueryField.id} (${item.id})`,
          sourceField: foundQueryField.id,
          fieldType: foundQueryField.type === "keywords" ? "keyword" : foundQueryField.type,
          hasHeaderOptions: true,
          showRenderTypeInSettings: false,
          filter: [item.id ?? ""],
          labels: [item.label ?? ""],
          renderType: ColumnRenderType.Number
        };
      }) ?? [];

      const currentColumnsLessOptions = columnModelState.columns.filter(colItem => {
        return colItem.sourceField !== foundQueryField.id;
      });

      updateColumnModelState({
        ...columnModelState,
        columns: [...currentColumnsLessOptions, ...optionsColumns]
      });
    }
  }

  async function getQueryInfo() {
    try {
      const response = await APIRequest("query/info", {}, {
        method: "POST",
        body: JSON.stringify({
          "dataset_id": socialDatasetId
        })
      });
      const newQueryInfo = new QueryInfo(response);

      return newQueryInfo;
    } catch(error) {
      console.error("ERROR:", error)
    }
  }

  useEffect(() => {
    if (!queryInfo) {
      getQueryInfo().then((res?: any) => {
        setQueryInfo(res);
        if ("fields" in res) {
          const countField = res?.fields.find((f: any) => f.id === "__count__");
          countField ? setDraftColumn({ field: countField }) : setDraftColumn(undefined);
        }
      });
    }
  }, [queryInfo]);

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      maxWidth="md"
      open={true}
      tabs={[i18n.t("display"), i18n.t("columns")]}
      title={i18n.t("widget-types.query-comparison-table.settings.title")}
    >
      <>
        {tabValue === 0 &&
          <SplitSettingsWrapper>
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                {settingsState.widgetTitle === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="customWidgetTitle"
                      label="Custom Text"
                      value={settingsState.customWidgetTitle} />
                  </Stack>
                }
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
                <SettingsToggleButton
                  keyid="density"
                  label={i18n.t("tables.row-density")}
                  optionsList={densityOptions}
                  value={settingsState.density} />
              </SettingsAccordion>
              <SettingsAccordion label={i18n.t("graph")}>
                <SettingsCheckbox
                  keyid="rowStripes"
                  label={i18n.t("tables.row-stripes")}
                  checked={settingsState.rowStripes}
                />
                {/* // Save for when combined row request is functional */}
                {/* {apiResponses && apiResponses.length > 1 && (
                  <SettingsCheckbox
                    keyid="displayAllQueriesRow"
                    label={i18n.t("widget-types.query-comparison-table.settings.display-combined-queries-row")}
                    checked={settingsState.displayAllQueriesRow}
                  />
                )} */}
              </SettingsAccordion>
            </SettingsOptions>
          </SplitSettingsWrapper>
        }
        {tabValue === 1 &&
          <>
            <ColumnManager
              columnModel={columnModelState}
              onColumnDialogMode={handleColumnDialogMode}
              onRemoveColumn={handleDeleteColumn}
              onRemoveMultipleColumns={deleteMultipleColumns}
              toolbarType={ColumnToolbarType.QUERY_COMPARISON_TABLE}
              updateColumnModel={updateColumnModelState}
              toolbar={queryInfo ?
                <QueryComparisonTableToolbar
                  canAddColumn={usuableFields && usuableFields?.length > 0}
                  onBulkAddFieldOptions={handleBulkAddFieldOptions}
                  onColumnDialogMode={(params: ColumnDialogParams) => {
                    handleColumnDialogMode(params);
                  }}
                /> :
                <Alert severity="warning">{i18n.t("api-request.missing-query-error")}</Alert>
              }
            >
            </ColumnManager>
            <ResponsiveDialog open={isDialogOpen} maxWidth="sm">
              {columnDialogMode && <DialogTitle>{i18n.t(`tables.${columnDialogMode}`)}</DialogTitle>}
              <DialogContent>
                <Alert severity="info" sx={{ marginBottom: 1 }}>
                  {i18n.t("widget-types.query-comparison-table.settings.available-fields")}
                </Alert>
                {queryInfo &&
                  <ComparisonTableDialogContent
                    activeColumnIndex={activeColumnIndex}
                    aggFieldsInUse={aggFieldsInUse}
                    columnDialogMode={columnDialogMode}
                    draftColumn={draftColumn}
                    onSetDraftColumn={handleSetDraftColumn}
                    queryInfo={queryInfo}
                    usuableFields={columnDialogMode === "update-column" ?
                      filteredQueryFields : usuableFields
                    }
                  />
                }
              </DialogContent>
              <DialogActions>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={handleCloseDialog}
                >
                  {i18n.t("cancel")}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => {
                    if (draftColumn) {
                      handleDialogSubmit({
                        columnDialogMode,
                        columnModelState,
                        draftColumn,
                        updateColumnModelState,
                        i18n
                      });
                    }
                    handleCloseDialog();
                  }}
                >
                  {columnDialogMode === "add-column" && i18n.t("add")}
                  {columnDialogMode === "update-column" && i18n.t("update")}
                </Button>
              </DialogActions>
            </ResponsiveDialog>
          </>
        }
      </>
    </TabbedDialog>
  );
}
